import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useEffectivePermissions, toSet, canEmitirBPe } from "../../hooks/useEffectivePermissions";
import { useWorkspaceSegment } from "../../hooks/useWorkspaceSegment";
import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { WorkspaceSegments } from "../../constants/workspaceSegments";
import {
  passagensByKeyFromConfigurationsResponse,
  hasAnyPassagensPartnerConfigured,
} from "../../constants/passagensConfigHelpers";
import CustomerDetails from "./CustomerDetails";
import RequestDetails from "./RequestDetails";
import Products from "./Products";
import History from "./History";
import AiInsights from "./AiInsights";
import ConversationTypeAssistant from "./ConversationTypeAssistant";
import PassagensBPe from "./PassagensBPe";
import RequestTeamDiscussionPanel from "./RequestTeamDiscussionPanel";
import LeadTasksSidebarBoard from "./LeadTasksSidebarBoard";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";

const accordionSummarySx = {
  "& .MuiAccordionSummary-content": {
    margin: "10px 0",
    minWidth: 0,
    overflow: "hidden",
    flexGrow: 1,
  },
};

const accordionTitleSx = {
  fontWeight: 700,
  fontSize: "1rem",
  lineHeight: 1.25,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
};

const ConversationDetails = ({
  selectedConversation,
  messages = [],
  onSelectedConversationUpdate,
  full = false,
  aiInsights,
  isLoadingInsights,
  aiInsightsError,
  onLoadAiInsights,
  onRefetchConversationDetails,
  onReloadMessages,
}) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [doubtsExpanded, setDoubtsExpanded] = useState(false);
  const [teamExpanded, setTeamExpanded] = useState(false);
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const { data: permissionsData } = useEffectivePermissions();
  const { segment, isLoading: isLoadingSegment } = useWorkspaceSegment();
  const { items: wsConfigItems, isLoading: isLoadingWsConfig } = useWorkspaceConfig();
  const scopesSet = toSet(permissionsData?.scopes);

  // Só exibe a venda de passagens no chat quando o workspace tem uma integração
  // de venda de passagens (ERP) configurada.
  const hasPassagensIntegration = useMemo(
    () =>
      hasAnyPassagensPartnerConfigured(
        passagensByKeyFromConfigurationsResponse({ data: { items: wsConfigItems || [] } })
      ),
    [wsConfigItems]
  );

  const canShowBPe =
    !isLoadingSegment &&
    !isLoadingWsConfig &&
    segment === WorkspaceSegments.PassengerTransport &&
    canEmitirBPe(scopesSet) &&
    hasPassagensIntegration;
  const canShowProducts = !isLoadingSegment && segment !== WorkspaceSegments.PassengerTransport;
  const canShowTeamDiscussion = scopesSet.has("team_chat.view");
  const canShowRequestTasks =
    scopesSet.has("team_tasks.view") || scopesSet.has("team_chat.view");

  useEffect(() => {
    // Sempre começa fechado ao trocar de conversa
    setInsightsExpanded(false);
    setDoubtsExpanded(false);
    setTeamExpanded(false);
    setTasksExpanded(false);
  }, [selectedConversation?.id]);

  useEffect(() => {
    if (teamExpanded) {
      void ensureNamespaceLoaded("team");
    }
  }, [teamExpanded]);

  useEffect(() => {
    if (canShowRequestTasks) {
      void ensureNamespaceLoaded("teamTasks");
    }
  }, [canShowRequestTasks]);

  return (
    <Box
      p={full ? 1 : 1.5}
      display="flex"
      flexDirection="column"
      gap={1}
      overflow="auto"
      className="sidebar-scroll"
      sx={{
        backgroundColor: theme.palette.background.default,
        // Em modo "full" (mobile), preenche apenas o painel disponível,
        // evitando usar 100vh e criando scroll duplo.
        height: full ? "100%" : "calc(100vh - 120px)",
      }}
    >
      {/* Cliente - sempre aberto inicialmente */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography component="span" sx={accordionTitleSx}>
            Cliente
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CustomerDetails
            selectedConversation={selectedConversation}
            onSelectedConversationUpdate={onSelectedConversationUpdate}
          />
        </AccordionDetails>
      </Accordion>

      {/* Detalhes da Solicitação - também aberto inicialmente */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography component="span" sx={accordionTitleSx}>
            {t("conversationDetails.request")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RequestDetails 
            key={selectedConversation?.id || "default"}
            selectedConversation={selectedConversation}
            onSelectedConversationUpdate={onSelectedConversationUpdate}
            onRefetchConversationDetails={onRefetchConversationDetails}
          />
        </AccordionDetails>
      </Accordion>

      {canShowTeamDiscussion && (
        <Accordion
          expanded={teamExpanded}
          onChange={(_, expanded) => setTeamExpanded(expanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            <Typography component="span" sx={accordionTitleSx}>
              {t("conversationDetails.teamDiscussion", { defaultValue: "Equipe" })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RequestTeamDiscussionPanel
              conversationId={selectedConversation?.id}
              active={teamExpanded}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {canShowRequestTasks && (
        <Accordion
          expanded={tasksExpanded}
          onChange={(_, expanded) => setTasksExpanded(expanded)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            <Typography component="span" sx={accordionTitleSx}>
              {t("conversationDetails.requestTasks", { defaultValue: "Tarefas" })}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <LeadTasksSidebarBoard
              conversationId={selectedConversation?.id}
              active={tasksExpanded}
              messages={messages}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Tirar dúvidas — fontes de conhecimento do tipo/subtipo */}
      <Accordion
        expanded={doubtsExpanded}
        onChange={(_, expanded) => setDoubtsExpanded(expanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography component="span" sx={accordionTitleSx}>
            {t("conversationDetails.askDoubts", { defaultValue: "Tirar dúvidas" })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {selectedConversation?.id ? (
            <ConversationTypeAssistant conversationId={selectedConversation.id} />
          ) : null}
        </AccordionDetails>
      </Accordion>

      {/* Insights da IA - sempre inicia FECHADO e carrega ao abrir */}
      <Accordion
        expanded={insightsExpanded}
        onChange={(_, expanded) => {
          setInsightsExpanded(expanded);
          if (expanded && onLoadAiInsights && selectedConversation) {
            onLoadAiInsights(selectedConversation.id);
          }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography component="span" sx={accordionTitleSx}>
            {t("conversationDetails.aiInsights")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AiInsights
            insights={aiInsights || selectedConversation?.aiInsights}
            isLoading={isLoadingInsights}
            error={aiInsightsError}
            onGenerate={
              onLoadAiInsights && selectedConversation
                ? () => onLoadAiInsights(selectedConversation.id)
                : undefined
            }
          />
        </AccordionDetails>
      </Accordion>

      {/* Passagens (BPe) – visível apenas com segmento rodoviário + permissão bpe.view
          + integração de venda de passagens (ERP) configurada no workspace */}
      {canShowBPe && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            <Typography component="span" sx={accordionTitleSx}>
              {t("conversationDetails.passagens")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PassagensBPe
              selectedConversation={selectedConversation}
              onReloadMessages={onReloadMessages}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {canShowProducts && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            <Typography component="span" sx={accordionTitleSx}>
              {t("conversationDetails.products")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Products
              selectedConversation={selectedConversation}
              onRefetchConversationDetails={onRefetchConversationDetails}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
          <Typography component="span" sx={accordionTitleSx}>
            {t("conversationDetails.history")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <History history={selectedConversation?.history} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ConversationDetails;
